import { v4 as uuidv4 } from 'uuid'
import { AxiosService } from './axios.service'
import { StorageService } from './storage.service'
import { dateHelper } from '../helpers/date.helper'
import { POST_PAYMENT_TIMINGS } from '../constants/post-payment.constants'

/**
 * Defines the API methods for the shopping cart
 */
export class CartService {
	static baseURL = 'carts'

	/**
	 * Creates a shopping cart on the MuviHub API and stores it, along with the
	 * session used to create it, in localStorage
	 * @param {Date|string} [returnDate] The return date, when the user is
	 * buying a round trip - omitted entirely for one-way purchases
	 * @param {{orderId: string, orderNumber?: number, ticketId: string}} [rebooking] The original
	 * order/ticket being rebooked - omitted for a regular purchase. `orderNumber` is only needed
	 * for a legacy-only order (bought before the native cart migration - `orderId` is then a
	 * synthetic guid with no row in the Muvi Hub `orders` table); it enables the server-side
	 * fallback to the legacy system, same pattern as `ClientTicketService.getOrder`. The
	 * created cart comes back with a `rebookingContext` (original ticket
	 * value + whether the difference is a charge/refund) when this is set.
	 * @return {Promise<any>} The created cart, including its id
	 */
	static createCart(returnDate, rebooking) {
		const params = {
			currency: 'BRL',
			sessionId: `ses_${StorageService.useSessionId.get()}`
		}

		if (returnDate) {
			params.returnDate = dateHelper.formatToYYYYMMDD(returnDate)
		}

		if (rebooking && rebooking.orderId && rebooking.ticketId) {
			params.rebooking = {
				originalOrderId: rebooking.orderId,
				originalTicketId: rebooking.ticketId
			}

			if (rebooking.orderNumber) {
				params.rebooking.originalOrderNumber = rebooking.orderNumber
			}
		}

		return AxiosService.postMuviHub(CartService.baseURL, params).then(({ data }) => {
			const cart = data.data

			StorageService.cart.set({ ...cart, sessionId: params.sessionId })
			return cart
		})
	}

	/**
	 * The id of the cart currently in progress, if any
	 * @return {string|undefined} The cart id
	 */
	static getCurrentCartId() {
		const cart = StorageService.cart.get()

		return cart && cart.id
	}

	/**
	 * Reads the current cart from the MuviHub API. Used when the server changed the cart on its own —
	 * spec 055 (D14): a refused re-reservation releases every seat of that fare class group, so the site
	 * has to re-read the cart instead of keeping seats that no longer exist.
	 * @param {string} [cartId] Defaults to the cart in progress
	 * @return {Promise<any>} The cart
	 */
	static getCart(cartId) {
		const id = cartId || CartService.getCurrentCartId()

		return AxiosService.getMuviHub(`${CartService.baseURL}/${id}`).then(
			({ data }) => data.data
		)
	}

	/**
	 * Applies a discount coupon to the current cart
	 * @param {string} code The coupon code
	 * @param {string} cpf The buyer's CPF, required by the API to validate the coupon
	 * @return {Promise<any>} { success, message, data: <cart summary: coupon, totals, poltronas, ...> }
	 */
	static applyCoupon(code, cpf) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cart && cart.id}/coupon`,
			{ code, cpf }
		).then(({ data }) => data)
	}

	/**
	 * Turns the travel insurance on/off for the current cart (payment step
	 * checkbox). The server only touches eligible seats - RODOSOFT legs with an
	 * insurance offer, outside public body sales - and reprices the cart.
	 *
	 * A new call cancels the previous one still pending (keep latest). Since a
	 * canceled request may still reach the server, `sequence` (increasing per
	 * click) lets the server ignore a superseded click that arrives late.
	 * @param {boolean} withInsurance Whether the customer accepted the insurance
	 * @param {number} [sequence] The click order - must grow on every call
	 * @return {Promise<any>} { success, message, data: <cart summary: totals, poltronas, userPricing, ...> }
	 */
	static setInsurance(withInsurance, sequence) {
		const cart = StorageService.cart.get()

		return AxiosService.putKeepLatestMuviHub(
			'cart-insurance',
			`${CartService.baseURL}/${cart && cart.id}/insurance`,
			{ withInsurance, sequence }
		).then(({ data }) => data)
	}

	/**
	 * Checks out the current cart, charging the given payment instrument
	 * @param {object} payload { payment, buyer, passengers, context }
	 * @return {Promise<any>} the checkout result
	 */
	static checkout(payload) {
		const cart = StorageService.cart.get()

		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cart && cart.id}/checkout`,
			payload,
			{
				headers: { 'X-Idempotency-Key': uuidv4() },
				// Spec 056 (US6): o checkout cria o pedido E cobra no legado. Sem teto
				// proprio, o request morria no default do axios e a tela dizia "a compra
				// nao sera cobrada" sobre um pedido que existia e estava pago (bug 8005).
				// T127 (D42): 60s, acima dos 45s que o Hub espera o legado + 10s da
				// recuperacao da reserva, para nao cortar uma resposta boa.
				timeout: POST_PAYMENT_TIMINGS.CHECKOUT_REQUEST_TIMEOUT_MS,
			}
		).then(({ data }) => data)
	}

	/**
	 * Checks out a rebooking cart: charges/refunds only the difference against
	 * the original ticket and links the new order to it (server-side) -
	 * `RebookingCheckoutHandler` (Muvi Hub) + `RebookingTicketCommand` (Moveflex)
	 * @param {string} cartId The new-trip cart id
	 * @param {object} payload { payment, buyer, passengers, context, rebooking: { originalOrderId, originalTicketId } }
	 * @return {Promise<any>} the checkout result - same shape as `checkout()`
	 */
	static rebook(cartId, payload) {
		return AxiosService.postMuviHub(
			`${CartService.baseURL}/${cartId}/rebook`,
			payload,
			{ headers: { 'X-Idempotency-Key': uuidv4() } }
		).then(({ data }) => data)
	}
}
